import { Button, ButtonProps } from '@compass/components'
import { Menu, MenuItem } from '@compass/Menu/Menu'
import { Popover } from '@compass/Popover'
import { useCompassToast } from 'hooks/toast'
import { Download, Ellipsis, Link as LinkIcon } from 'lucide-react'
import { ReactNode } from 'react'
import { MenuTrigger } from 'react-aria-components'

import { copyCurrentUrlToClipboard } from './utils'

type ActionItem = {
  label: string
  icon: ReactNode
  isDisabled?: boolean
  onPress: () => void
  tooltip: ReactNode
  variant?: ButtonProps['variant']
}

export function BotDetectionEventsActions({
  isExportDisabled,
  onExport,
  showExport = true,
}: {
  isExportDisabled: boolean
  onExport: () => void
  showExport?: boolean
}) {
  const { showToast } = useCompassToast()

  const handleCopyLink = () => copyCurrentUrlToClipboard(showToast)

  const actions: ActionItem[] = [
    ...(showExport
      ? [
          {
            label: 'Export',
            isDisabled: isExportDisabled,
            onPress: onExport,
            icon: <Download size={16} />,
            tooltip: 'Export bot detection events as a .csv file including any filters currently applied',
          },
        ]
      : []),
    {
      label: 'Copy link',
      onPress: handleCopyLink,
      icon: <LinkIcon size={16} />,
      tooltip: 'Copy the URL with filters applied. Only your team can access this link.',
    },
  ]

  return (
    <>
      <MenuTrigger>
        <Button className='md:hidden' isIcon variant='secondary' aria-label='Show bot detection events actions'>
          <Ellipsis />
        </Button>
        <Popover placement='bottom end' className='w-fit min-w-37.5'>
          <Menu>
            {actions.map(({ label, icon, isDisabled, onPress }) => (
              <MenuItem key={label} aria-label={label} prefix={icon} isDisabled={isDisabled} onAction={onPress}>
                {label}
              </MenuItem>
            ))}
          </Menu>
        </Popover>
      </MenuTrigger>

      {actions.map((action) => (
        <ActionMenuItem key={action.label} {...action} className='hidden md:flex' />
      ))}
    </>
  )
}

function ActionMenuItem({
  label,
  variant = 'secondary',
  isDisabled,
  onPress,
  icon,
  tooltip,
  className,
}: ActionItem & {
  className?: string
}) {
  return (
    <Button
      variant={variant}
      isDisabled={isDisabled}
      aria-label={label}
      onPress={onPress}
      tooltip={tooltip}
      className={className}
    >
      {icon}
      {label}
    </Button>
  )
}
